// Alert dispatcher — a small zustand store managing an in-flight queue
// of toast-style alerts shown over the map. Boss feedback 2026-05-10:
//
//   * 'Terrain Ahead' when AGL goes below a threshold
//   * 'Approaching runway X' when GPS comes within ~3 NM of an airport
//     in the active flight plan
//   * 'Destination weather frequency' when nearing the destination
//
// The engine handles queueing, deduplication (same alert id within
// the cooldown window is suppressed), auto-dismiss, and severity-based
// styling. Triggers live elsewhere (alertTriggers.ts) and call
// useAlertStore.getState().push(...) when their condition fires.

import { create } from 'zustand';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface AlertItem {
  /** Stable id used for dedupe. Same id within `dedupeMs` is\n   *  suppressed. */
  id: string;
  severity: AlertSeverity;
  /** Short headline (≤ ~40 chars). */
  title: string;
  /** Optional secondary line (≤ ~80 chars). */
  body?: string;
  /** Auto-dismiss after this many ms. 0 = sticky until dismissed. */
  ttlMs: number;
  /** When the alert was first pushed (epoch ms). */
  pushedAtMs: number;
  /** Optional CTA: clicking it runs `onAction` and dismisses. */
  actionLabel?: string;
  onAction?: () => void;
}

interface AlertState {
  /** Active alerts — most recent first. */
  alerts: AlertItem[];
  /** Last-seen timestamp per id, used for dedupe. */
  lastSeenById: Record<string, number>;

  push: (a: Omit<AlertItem, 'pushedAtMs'> & { dedupeMs?: number }) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const DEFAULT_DEDUPE_MS = 30_000;
const MAX_VISIBLE = 4;

export const useAlertStore = create<AlertState>((set, get) => ({
  alerts: [],
  lastSeenById: {},

  push: (a) => {
    const now = Date.now();
    const lastSeen = get().lastSeenById[a.id] ?? 0;
    const dedupeMs = a.dedupeMs ?? DEFAULT_DEDUPE_MS;
    if (now - lastSeen < dedupeMs) return; // still cooling down

    set((s) => {
      // Drop any existing alert with this id (we're replacing it).
      const filtered = s.alerts.filter((x) => x.id !== a.id);
      // Cap visible to MAX_VISIBLE (drop the oldest).
      const next = [
        { ...a, pushedAtMs: now } as AlertItem,
        ...filtered.slice(0, MAX_VISIBLE - 1),
      ];
      return {
        alerts: next,
        lastSeenById: { ...s.lastSeenById, [a.id]: now },
      };
    });

    // Auto-dismiss timer (browser-only; safe no-op SSR).
    if (a.ttlMs > 0 && typeof window !== 'undefined') {
      window.setTimeout(() => get().dismiss(a.id), a.ttlMs);
    }
  },

  dismiss: (id) => set((s) => ({
    alerts: s.alerts.filter((x) => x.id !== id),
  })),

  dismissAll: () => set({ alerts: [] }),
}));
